import { useEffect, useMemo, useRef, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";

import { getPreparedProducts } from "../../elaborados/api/preparedProductsApi";
import { getIngredients } from "../../insumos/api/ingredientsApi";
import { getRecipes } from "../../recetas/api/recipesApi";
import {
  componentKey,
  rowsFromResponse,
  UNIT_LABELS,
} from "../utils/saleProductForm.utils";

const SEARCH_DELAY_MS = 150;
const RESULTS_PER_TYPE = 8;

function errorToMessage(error) {
  return error?.data?.message || error?.message || "No se pudieron buscar componentes.";
}

function sectorsLabel(value) {
  const sectors = Array.isArray(value) ? value : String(value || "").split(",");
  return sectors.filter(Boolean).join(" y ") || "Sin sector";
}

function normalizeIngredient(item) {
  return {
    kind: "ingredient",
    group: "Insumos",
    id: Number(item.id),
    name: item.name || `Insumo #${item.id}`,
    unit: item.baseUnit || "",
    unitLabel: UNIT_LABELS[item.baseUnit] || item.baseUnit || "sin unidad",
    sectorLabel: sectorsLabel(item.sector),
    isAvailable: item.isActive !== false,
  };
}

function normalizePreparedProduct(item) {
  return {
    kind: "preparedProduct",
    group: "Elaborados",
    id: Number(item.id),
    name: item.name || `Elaborado #${item.id}`,
    unit: item.resultUnit || "",
    unitLabel: UNIT_LABELS[item.resultUnit] || item.resultUnit || "sin unidad",
    sectorLabel: sectorsLabel(item.sector),
    isAvailable: item.isActive !== false,
  };
}

function normalizeRecipe(item) {
  return {
    kind: "recipe",
    group: "Recetas",
    id: Number(item.id),
    name: item.name || `Receta #${item.id}`,
    unit: "portion",
    unitLabel: "porciones",
    sectorLabel: sectorsLabel(item.sector),
    isAvailable: item.isActive !== false,
  };
}

function componentRequests(query, page, signal) {
  const common = {
    page,
    limit: RESULTS_PER_TYPE,
    name: query,
    isActive: true,
    signal,
  };
  return Promise.allSettled([
    getIngredients(common),
    getPreparedProducts(common),
    getRecipes(common),
  ]);
}

const RESULT_NORMALIZERS = [
  normalizeIngredient,
  normalizePreparedProduct,
  normalizeRecipe,
];
const GROUP_ORDER = { Insumos: 0, Elaborados: 1, Recetas: 2 };

function optionsFromRequests(requests) {
  return requests.flatMap((request, index) => {
    if (request.status !== "fulfilled") return [];
    return rowsFromResponse(request.value)
      .filter((item) => item?.id)
      .map(RESULT_NORMALIZERS[index]);
  });
}

function responseHasNextPage(response, page) {
  const meta = response?.meta;
  if (typeof meta?.hasNextPage === "boolean") return meta.hasNextPage;
  if (Number.isFinite(Number(meta?.totalPages))) {
    return page < Number(meta.totalPages);
  }
  return rowsFromResponse(response).length === RESULTS_PER_TYPE;
}

function requestsHaveNextPage(requests, page) {
  return requests.some(
    (request) =>
      request.status === "fulfilled" && responseHasNextPage(request.value, page)
  );
}

function requestsError(requests) {
  return requests
    .filter((request) => request.status === "rejected")
    .map((request) => errorToMessage(request.reason))
    .join(" | ");
}

export default function SaleProductComponentSearch({ selected = [], disabled, onSelect }) {
  const latestRequestId = useRef(0);
  const loadMoreController = useRef(null);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState("");
  const selectedKeys = useMemo(
    () => new Set(selected.map(componentKey)),
    [selected]
  );
  const visibleOptions = options
    .filter((option) => !selectedKeys.has(componentKey(option)))
    .sort((left, right) => {
      const groupDifference = GROUP_ORDER[left.group] - GROUP_ORDER[right.group];
      return groupDifference || left.name.localeCompare(right.name, "es");
    });

  useEffect(() => {
    const query = inputValue.trim();
    loadMoreController.current?.abort();
    setLoadingMore(false);
    if (!query) {
      latestRequestId.current += 1;
      setOptions([]);
      setPage(1);
      setHasMore(false);
      setLoading(false);
      setError("");
      return undefined;
    }

    const controller = new AbortController();
    const requestId = ++latestRequestId.current;
    setOptions([]);
    setPage(1);
    setHasMore(false);
    setLoading(true);
    setError("");
    const timeout = setTimeout(async () => {
      const requests = await componentRequests(query, 1, controller.signal);
      if (controller.signal.aborted || requestId !== latestRequestId.current) return;

      setOptions(optionsFromRequests(requests));
      setHasMore(requestsHaveNextPage(requests, 1));
      setError(requestsError(requests));
      setLoading(false);
    }, SEARCH_DELAY_MS);

    return () => {
      clearTimeout(timeout);
      controller.abort();
      loadMoreController.current?.abort();
    };
  }, [inputValue]);

  async function loadNextPage() {
    const query = inputValue.trim();
    if (!query || disabled || loading || loadingMore || !hasMore) return;

    const requestId = latestRequestId.current;
    const nextPage = page + 1;
    const controller = new AbortController();
    loadMoreController.current?.abort();
    loadMoreController.current = controller;
    setLoadingMore(true);

    try {
      const requests = await componentRequests(query, nextPage, controller.signal);
      if (controller.signal.aborted || requestId !== latestRequestId.current) return;

      const additions = optionsFromRequests(requests);
      setOptions((current) => {
        const byKey = new Map(current.map((option) => [componentKey(option), option]));
        additions.forEach((option) => byKey.set(componentKey(option), option));
        return Array.from(byKey.values());
      });
      setPage(nextPage);
      setHasMore(requestsHaveNextPage(requests, nextPage));
      setError(requestsError(requests));
    } finally {
      if (!controller.signal.aborted && requestId === latestRequestId.current) {
        setLoadingMore(false);
      }
    }
  }

  function handleListScroll(event) {
    const list = event.currentTarget;
    const distanceToBottom = list.scrollHeight - list.scrollTop - list.clientHeight;
    if (distanceToBottom <= 48) loadNextPage();
  }

  return (
    <Autocomplete
      value={null}
      inputValue={inputValue}
      options={visibleOptions}
      loading={loading || loadingMore}
      disabled={disabled}
      open={Boolean(inputValue.trim())}
      filterOptions={(items) => items}
      groupBy={(option) => option.group}
      getOptionDisabled={(option) => !option.isAvailable}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(left, right) => componentKey(left) === componentKey(right)}
      slotProps={{ listbox: { onScroll: handleListScroll } }}
      noOptionsText={error || "No se encontraron componentes"}
      loadingText="Buscando componentes..."
      onInputChange={(_, value, reason) => {
        if (reason === "input" || reason === "clear") setInputValue(value);
      }}
      onChange={(_, option) => {
        if (!option) return;
        onSelect?.(option);
        setInputValue("");
        setOptions([]);
      }}
      renderGroup={(params) => (
        <li key={params.key}>
          <Box component="div" sx={{ px: 1.5, py: 0.7, bgcolor: "secondary.100" }}>
            <Typography sx={{ fontSize: 12, fontWeight: 900 }}>{params.group}</Typography>
          </Box>
          <Box component="ul" sx={{ p: 0, m: 0 }}>{params.children}</Box>
        </li>
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <Box component="li" key={`${option.kind}:${option.id}`} {...optionProps}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 800 }}>
                {option.name}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: "neutral.600" }}>
                {option.sectorLabel} · {option.unitLabel}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 0.6, alignItems: "center" }}>
              <Chip label={option.group.slice(0, -1)} size="small" sx={{ fontSize: 10 }} />
              <Chip
                label={option.isAvailable ? "Disponible" : "No disponible"}
                size="small"
                sx={{
                  fontSize: 9.5,
                  fontWeight: 800,
                  color: option.isAvailable
                    ? "rgba(18, 111, 63, 1)"
                    : "rgba(160, 49, 49, 1)",
                  bgcolor: option.isAvailable
                    ? "rgba(33, 150, 83, 0.16)"
                    : "rgba(195, 60, 60, 0.16)",
                }}
              />
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Buscar insumo, elaborado o receta"
          error={Boolean(error)}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon sx={{ ml: 1, mr: 0.5, fontSize: 19 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading || loadingMore ? <CircularProgress size={17} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "white" } }}
        />
      )}
    />
  );
}
